import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import AddIcon from "@mui/icons-material/Add";
import BusinessIcon from "@mui/icons-material/Business";
import CampaignIcon from "@mui/icons-material/Campaign";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import HealthAndSafetyIcon from "@mui/icons-material/HealthAndSafety";
import HotelIcon from "@mui/icons-material/Hotel";
import InfoIcon from "@mui/icons-material/Info";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import LanguageIcon from "@mui/icons-material/Language";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import NotificationsIcon from "@mui/icons-material/Notifications";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import SchoolIcon from "@mui/icons-material/School";
import SearchIcon from "@mui/icons-material/Search";
import SettingsIcon from "@mui/icons-material/Settings";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import SupportIcon from "@mui/icons-material/Support";
import TitleIcon from "@mui/icons-material/Title";
import WarningIcon from "@mui/icons-material/Warning";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputBase,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  styled,
  Tooltip,
  Typography,
} from "@mui/material";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAccount, useToast } from "../../../context";
import { Campaign, CampaignTemplate, Template } from "../../../interfaces/all";
import {
  assignTemplateToCampaign,
  getAllCampaigns,
  getCampaignsWithTemplates,
  getTemplateParameters,
  getTemplates,
  removeTemplateFromCampaign,
} from "../../../services/api";
import { apiService } from "../../../services/axios";
import { ParameterConfig } from "../../../types/template-config";
import { ErrorHandler } from "../../../utils/error-handler";
import Header from "../../chat/Header/Header";
import { TemplateSkeleton } from "../../shared/SkeletonLoader";
import TemplateParameterConfigurator from "./TemplateParameterConfigurator";
import TemplatePreview from "./TemplatePreview";

const Container = styled(Box)`
  width: 100%;
  height: 100%;
  background: #fff;
  border-right: 1px solid #e9edef;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;

  @media (max-width: 600px) {
    width: 100%;
    min-width: 100%;
    max-width: 100%;
  }
`;

const ContentContainer = styled(Box)`
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 60px);
`;

const BoxWrapper = styled(Box)`
  background: #ffffff;
  padding: 16px 16px;
  margin-bottom: 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
`;

const CategoryIcon = styled(Avatar)<{ category: string }>`
  width: 28px;
  height: 28px;
  font-size: 14px;
  background-color: ${(props) => {
    switch (props.category.toLowerCase()) {
      case "marketing":
        return "#008069";
      case "utility":
        return "#4caf50";
      case "authentication":
        return "#ff9800";
      case "support":
        return "#9c27b0";
      case "finance":
        return "#f44336";
      case "shopping":
        return "#e91e63";
      case "travel":
        return "#00bcd4";
      case "healthcare":
        return "#8bc34a";
      case "education":
        return "#795548";
      case "food":
        return "#ff5722";
      case "real_estate":
        return "#607d8b";
      default:
        return "#008069";
    }
  }};
  color: white;
  margin-right: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;

  &:hover {
    transform: scale(1.1);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
  }
`;

const getCategoryIcon = (category: string) => {
  switch (category.toLowerCase()) {
    case "marketing":
      return <CampaignIcon fontSize="small" />;
    case "utility":
      return <NotificationsIcon fontSize="small" />;
    case "authentication":
      return <SupportIcon fontSize="small" />;
    case "support":
      return <SupportIcon fontSize="small" />;
    case "finance":
      return <AccountBalanceIcon fontSize="small" />;
    case "shopping":
      return <ShoppingCartIcon fontSize="small" />;
    case "travel":
      return <LocalShippingIcon fontSize="small" />;
    case "healthcare":
      return <HealthAndSafetyIcon fontSize="small" />;
    case "education":
      return <SchoolIcon fontSize="small" />;
    case "food":
      return <RestaurantIcon fontSize="small" />;
    case "real_estate":
      return <BusinessIcon fontSize="small" />;
    case "hotel":
      return <HotelIcon fontSize="small" />;
    default:
      return <BusinessIcon fontSize="small" />;
  }
};

const getCategoryName = (category: string) => {
  switch (category.toLowerCase()) {
    case "marketing":
      return "Marketing";
    case "utility":
      return "Utilitário";
    case "authentication":
      return "Autenticação";
    case "support":
      return "Suporte";
    case "finance":
      return "Financeiro";
    case "shopping":
      return "Compras";
    case "travel":
      return "Viagem";
    case "healthcare":
      return "Saúde";
    case "education":
      return "Educação";
    case "food":
      return "Alimentação";
    case "real_estate":
      return "Imobiliário";
    case "hotel":
      return "Hotelaria";
    default:
      return category;
  }
};

const getLanguageName = (language: string) => {
  switch (language.toLowerCase()) {
    case "pt_br":
    case "pt":
      return "Português (BR)";
    case "en":
    case "en_us":
      return "English (US)";
    case "es":
    case "es_es":
      return "Español (ES)";
    case "fr":
    case "fr_fr":
      return "Français (FR)";
    case "de":
    case "de_de":
      return "Deutsch (DE)";
    case "it":
    case "it_it":
      return "Italiano (IT)";
    case "ar":
      return "العربية (AR)";
    case "hi":
      return "हिन्दी (IN)";
    case "ja":
      return "日本語 (JP)";
    case "ko":
      return "한국어 (KR)";
    case "zh":
    case "zh_cn":
      return "中文 (CN)";
    default:
      return language;
  }
};

interface TemplatesSidebarProps {
  isOpen: boolean;
  showSkeleton?: boolean;
}

const TemplatesSidebar: React.FC<TemplatesSidebarProps> = ({
  isOpen,
  showSkeleton = false,
}) => {
  const { account } = useAccount();
  const { showError, showToast } = useToast();
  const [templates, setTemplates] = useState<Template[]>([]);
  const [campaignTemplates, setCampaignTemplates] = useState<
    CampaignTemplate[]
  >([]);
  const [loading, setLoading] = useState(false); // Iniciar como false para evitar renderização prematura
  const [error, setError] = useState<string | null>(null);
  const [showAssignmentDialog, setShowAssignmentDialog] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(
    null,
  );
  const [selectedCampaign, setSelectedCampaign] = useState<string>("");
  const [campaigns, setCampaigns] = useState<{ id: string; name: string }[]>(
    [],
  );
  const [assigning, setAssigning] = useState(false);
  const [removingTemplate, setRemovingTemplate] = useState<string | null>(null);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [templateToRemove, setTemplateToRemove] = useState<{
    templateId: string;
    campaignId: string;
  } | null>(null);

  // Estados para busca e filtros
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [sortBy] = useState<string>("name");
  const [showDetails, setShowDetails] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages] = useState(1);

  // Estados para configuração de parâmetros
  const [showParameterConfig, setShowParameterConfig] = useState(false);
  const [templateForConfig, setTemplateForConfig] = useState<Template | null>(
    null,
  );

  // Estados para preview do template
  const [templateParameters, setTemplateParameters] = useState<any>(null);
  const [loadingParameters, setLoadingParameters] = useState(false);
  const [parameterConfigs, setParameterConfigs] = useState<ParameterConfig[]>(
    [],
  );

  // Usar ref para evitar recriação do useCallback quando showError mudar
  const showErrorRef = useRef(showError);
  useEffect(() => {
    showErrorRef.current = showError;
  }, [showError]);

  const fetchTemplates = useCallback(async () => {
    const currentCompanyId = account?.company_id;

    if (!currentCompanyId) {
      setError("ID da empresa não encontrado");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const [templatesResponse, campaignsResponse, allCampaignsResponse] =
        await Promise.all([
          getTemplates(currentCompanyId),
          getCampaignsWithTemplates(),
          getAllCampaigns(),
        ]);

      // Verificar se o company_id ainda é o mesmo antes de atualizar estado
      // Isso previne race conditions quando o componente fecha/abre rapidamente
      if (account?.company_id !== currentCompanyId) {
        return; // Componente foi fechado ou company_id mudou, não atualizar estado
      }

      if (
        templatesResponse.status === "success" &&
        templatesResponse.data?.data
      ) {
        setTemplates(templatesResponse.data.data as unknown as Template[]);
      } else {
        setTemplates([]);
      }

      if (campaignsResponse.status === "success" && campaignsResponse.data) {
        setCampaignTemplates(
          campaignsResponse.data as unknown as CampaignTemplate[],
        );
      } else {
        setCampaignTemplates([]);
      }

      // Buscar todas as campanhas disponíveis
      if (
        allCampaignsResponse.status === "success" &&
        allCampaignsResponse.data
      ) {
        const data = allCampaignsResponse.data as unknown as {
          campaigns: Campaign[];
        };
        const allCampaigns = data.campaigns.map((campaign: Campaign) => ({
          id: campaign.id,
          name: campaign.name,
        }));
        setCampaigns(allCampaigns);
      } else {
        setCampaigns([]);
      }
    } catch (err) {
      // Verificar se ainda estamos no mesmo contexto antes de atualizar erro
      if (account?.company_id !== currentCompanyId) {
        return; // Componente foi fechado ou company_id mudou, não atualizar estado
      }

      // Usar ErrorHandler para obter mensagem específica do backend
      const errorResponse = ErrorHandler.handleApiError(err);
      const errorMessage =
        errorResponse.userMessage ||
        errorResponse.message ||
        "Erro ao carregar templates";

      setError(errorMessage);
      showErrorRef.current(errorMessage);
    } finally {
      // Verificar se ainda estamos no mesmo contexto antes de atualizar loading
      if (account?.company_id === currentCompanyId) {
        setLoading(false);
      }
    }
  }, [account?.company_id]); // Removido showError das dependências

  // Usar ref para controlar se já está buscando para evitar múltiplas chamadas
  const isFetchingRef = useRef(false);

  useEffect(() => {
    if (isOpen && !isFetchingRef.current) {
      // Limpar dados antigos antes de buscar novamente
      setTemplates([]);
      setCampaignTemplates([]);
      setCampaigns([]);
      setError(null);
      setLoading(true);
      isFetchingRef.current = true;

      fetchTemplates().finally(() => {
        isFetchingRef.current = false;
      });
    } else if (!isOpen) {
      // Limpar todos os dados quando fechar para evitar estado inconsistente
      setTemplates([]);
      setCampaignTemplates([]);
      setCampaigns([]);
      setError(null);
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, [fetchTemplates, isOpen]);

  const handleAssignTemplate = (template: Template) => {
    setSelectedTemplate(template);
    setShowAssignmentDialog(true);
  };

  // Função para mostrar detalhes do template
  const handleShowDetails = async (template: Template) => {
    setSelectedTemplate(template);
    setShowDetails(true);

    // Carregar parâmetros do template para o preview
    try {
      setLoadingParameters(true);
      const response = await getTemplateParameters(
        template.id,
        template.name,
        account?.company_id,
      );

      if (response.status === "success" && response.data) {
        setTemplateParameters(response.data);

        // Inicializar configurações de parâmetros com valores padrão
        const templateData = response.data as any;
        if (templateData.requiredParametersCount > 0) {
          const configs: ParameterConfig[] = [];
          for (let i = 0; i < templateData.requiredParametersCount; i++) {
            configs.push({
              index: i,
              type: "dynamic",
              value: "",
              dynamicField: "clientName",
            });
          }
          setParameterConfigs(configs);
        } else {
          setParameterConfigs([]);
        }
      } else {
        setTemplateParameters(null);
        setParameterConfigs([]);
      }
    } catch (err) {
      console.error("Erro ao buscar parâmetros do template:", err);
      setTemplateParameters(null);
      setParameterConfigs([]);
    } finally {
      setLoadingParameters(false);
    }
  };

  // Função para fechar detalhes
  const handleCloseDetails = () => {
    setShowDetails(false);
    setSelectedTemplate(null);
    setTemplateParameters(null);
    setParameterConfigs([]);
  };

  // Função para ir para página anterior
  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  // Função para ir para próxima página
  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handleConfirmAssignment = async () => {
    if (!selectedTemplate || !selectedCampaign) return;

    // Verificar se o template já está atribuído a esta campanha
    const isAlreadyAssigned = campaignTemplates.some(
      (ct) =>
        ct.template_id === selectedTemplate.id &&
        ct.campaign_id === selectedCampaign &&
        ct.is_active,
    );

    if (isAlreadyAssigned) {
      showError(new Error("Este template já está atribuído a esta campanha"));
      return;
    }

    try {
      setAssigning(true);
      const campaignName =
        campaigns.find((c) => c.id === selectedCampaign)?.name ||
        selectedCampaign;

      const response = await assignTemplateToCampaign({
        campaignId: selectedCampaign,
        campaignName,
        templateId: selectedTemplate.id,
        templateName: selectedTemplate.name,
        templateStatus: selectedTemplate.status,
        templateCategory: selectedTemplate.category,
        templateLanguage: selectedTemplate.language,
        assignedBy: account?.name || "Sistema",
      });

      if (response.status === "success") {
        showToast({
          type: "success",
          message: "Template atribuído à campanha com sucesso",
        });
        setShowAssignmentDialog(false);
        setSelectedTemplate(null);
        setSelectedCampaign("");
        fetchTemplates(); // Recarregar dados
      }
    } catch (err) {
      showError(err);
    } finally {
      setAssigning(false);
    }
  };

  const handleRemoveTemplate = async (
    templateId: string,
    campaignId: string,
  ) => {
    setTemplateToRemove({ templateId, campaignId });
    setShowRemoveConfirm(true);
  };

  const confirmRemoveTemplate = async () => {
    if (!templateToRemove) return;

    setRemovingTemplate(templateToRemove.templateId);

    try {
      const response = await removeTemplateFromCampaign(
        templateToRemove.campaignId,
        templateToRemove.templateId,
      );

      if (response.status === "success") {
        showToast({
          type: "success",
          message: "Template removido da campanha com sucesso",
        });
        fetchTemplates(); // Recarregar dados
      }
    } catch (err) {
      console.error("[DEBUG][handleRemoveTemplate] error:", err);
      showError(err);
    } finally {
      setRemovingTemplate(null);
      setShowRemoveConfirm(false);
      setTemplateToRemove(null);
    }
  };

  const handleConfigureParameters = (template: Template) => {
    setTemplateForConfig(template);
    setShowParameterConfig(true);
  };

  const handleCloseParameterConfig = () => {
    setShowParameterConfig(false);
    setTemplateForConfig(null);
  };

  const handleSaveParameterConfig = async (config: ParameterConfig[]) => {
    if (!templateForConfig || !account?.company_id) {
      console.error("Template ou dados do usuário não encontrados");
      return;
    }

    try {
      // Preparar dados para salvar
      const configData = {
        templateId: templateForConfig.id,
        templateName: templateForConfig.name,
        parameters: config.map((configItem) => ({
          index: configItem.index,
          type: configItem.type,
          value: configItem.value || "",
          dynamicField:
            configItem.type === "fixed"
              ? null
              : (configItem.dynamicField ?? "clientName"),
        })),
      };

      // Salvar no backend
      await apiService.post("/template-configs/save", {
        templateId: templateForConfig.id,
        templateName: templateForConfig.name,
        companyId: account.company_id,
        parameters: configData.parameters,
      });

      console.log("Configuração de parâmetros salva com sucesso!");
    } catch (error) {
      console.error("Erro ao salvar configuração:", error);
    }
  };

  const hasTemplateVariables = (template: Template) => {
    // Verificar se o template tem componentes com parâmetros
    if (template.components && Array.isArray(template.components)) {
      return template.components.some((component: any) => {
        if (component.text) {
          // Verificar se há placeholders como {{1}}, {{2}}, etc.
          return /\{\{\d+\}\}/.test(component.text);
        }
        return false;
      });
    }
    return false;
  };

  const getAssignedCampaigns = (templateId: string) => {
    const filtered = campaignTemplates.filter((ct) => {
      return ct.template_id === templateId && ct.is_active;
    });

    return filtered.map((ct) => ({
      campaignId: ct.campaign_id,
      campaignName: ct.campaign_name,
      assignedBy: ct.assigned_by,
      assignedAt: ct.assigned_at,
    }));
  };

  // Funções de filtro e busca
  const filteredAndSortedTemplates = useMemo(() => {
    const filtered = templates.filter((template) => {
      // Filtro por busca
      const matchesSearch =
        searchTerm === "" ||
        template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        getCategoryName(template.category)
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        getLanguageName(template.language)
          .toLowerCase()
          .includes(searchTerm.toLowerCase());

      // Filtro por status
      const matchesStatus =
        statusFilter === "all" || template.status === statusFilter;

      // Filtro por categoria
      const matchesCategory =
        categoryFilter === "all" ||
        template.category.toLowerCase() === categoryFilter.toLowerCase();

      return matchesSearch && matchesStatus && matchesCategory;
    });

    // Ordenação
    filtered.sort((a, b) => {
      switch (sortBy) {
        case "name":
          return a.name.localeCompare(b.name);
        case "status":
          return a.status.localeCompare(b.status);
        case "category":
          return getCategoryName(a.category).localeCompare(
            getCategoryName(b.category),
          );
        case "language":
          return getLanguageName(a.language).localeCompare(
            getLanguageName(b.language),
          );
        case "date":
          return 0; // Ordenação por data não disponível
        default:
          return a.name.localeCompare(b.name);
      }
    });

    return filtered;
  }, [templates, searchTerm, statusFilter, categoryFilter, sortBy]);

  // Categorias únicas para filtros
  const uniqueCategories = useMemo(() => {
    const categories = [...new Set(templates.map((t) => t.category))];
    return categories.sort((a, b) =>
      getCategoryName(a).localeCompare(getCategoryName(b)),
    );
  }, [templates]);

  // Não renderizar nada se não estiver aberto
  if (!isOpen) {
    return null;
  }

  if (showSkeleton) {
    return (
      <Container>
        <Box sx={{ height: "60px", flexShrink: 0 }}>
          <Header title="Templates do WhatsApp" />
        </Box>
        <ContentContainer>
          {/* Filtros */}
          <Box
            sx={{
              p: 2,
              backgroundColor: "#f0f2f5",
              borderBottom: "1px solid #e9edef",
              flexShrink: 0,
            }}
          >
            <Stack spacing={2}>
              <Skeleton
                variant="rectangular"
                width="100%"
                height={40}
                sx={{ borderRadius: "8px" }}
              />
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 1,
                }}
              >
                <Skeleton
                  variant="rectangular"
                  width="100%"
                  height={36}
                  sx={{ borderRadius: "8px" }}
                />
                <Skeleton
                  variant="rectangular"
                  width="100%"
                  height={36}
                  sx={{ borderRadius: "8px" }}
                />
              </Box>
              <Skeleton
                variant="rectangular"
                width="100%"
                height={36}
                sx={{ borderRadius: "8px" }}
              />
            </Stack>
          </Box>

          {/* Lista */}
          <BoxWrapper>
            {Array.from({ length: 6 }).map((_, index) => (
              <TemplateSkeleton key={`template-skeleton-${index}`} />
            ))}
          </BoxWrapper>
        </ContentContainer>
      </Container>
    );
  }

  return (
    <Container>
      <Box sx={{ height: "60px", flexShrink: 0 }}>
        <Header title="Templates do WhatsApp" />
      </Box>

      <ContentContainer>
        {/* Erro - apenas quando não está carregando */}
        {error && !loading && (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        )}

        {/* Filtros avançados - sempre visíveis */}
        <Box
          sx={{
            p: 2,
            backgroundColor: "#f0f2f5",
            borderBottom: "1px solid #e9edef",
            flexShrink: 0,
          }}
        >
          <Stack spacing={2}>
            {/* Campo de busca */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                backgroundColor: "#fff",
                borderRadius: "8px",
                padding: "4px 8px",
                border: "1px solid #e9edef",
                "&:focus-within": {
                  borderColor: "#00a884",
                  boxShadow: "0 0 0 2px rgba(0, 168, 132, 0.1)",
                },
              }}
            >
              <SearchIcon sx={{ color: "#54656f", fontSize: 20, mr: 1 }} />
              <InputBase
                placeholder="Buscar por nome, categoria ou idioma..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{
                  flex: 1,
                  fontSize: "14px",
                  color: "#111b21",
                  "&::placeholder": {
                    color: "#667781",
                  },
                }}
              />
            </Box>

            {/* Filtros em linha */}
            <Box
              sx={{
                display: "flex",
                gap: 2,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              {/* Filtro por Status */}
              <FormControl size="small" sx={{ flex: 1 }}>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  displayEmpty
                  sx={{
                    backgroundColor: "#fff",
                    borderRadius: "6px",
                    fontSize: "13px",
                    width: "100%",
                    "& .MuiSelect-select": {
                      padding: "8px 12px",
                      fontSize: "13px",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#e9edef",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#00a884",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#00a884",
                    },
                  }}
                >
                  <MenuItem value="all">Todos os Status</MenuItem>
                  <MenuItem value="APPROVED">Aprovados</MenuItem>
                  <MenuItem value="PENDING">Pendentes</MenuItem>
                  <MenuItem value="REJECTED">Rejeitados</MenuItem>
                </Select>
              </FormControl>

              {/* Filtro por Categoria */}
              <FormControl size="small" sx={{ flex: 1 }}>
                <Select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  displayEmpty
                  sx={{
                    backgroundColor: "#fff",
                    borderRadius: "6px",
                    fontSize: "13px",
                    width: "100%",
                    "& .MuiSelect-select": {
                      padding: "8px 12px",
                      fontSize: "13px",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#e9edef",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#00a884",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#00a884",
                    },
                  }}
                >
                  <MenuItem value="all">Todas as Categorias</MenuItem>
                  {uniqueCategories.map((category) => (
                    <MenuItem key={category} value={category}>
                      {getCategoryName(category)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Stack>
        </Box>

        {/* Cards de templates - com scroll apenas nos cards */}
        {loading ? (
          <Box
            sx={{
              flex: 1,
              overflow: "hidden",
              p: 1,
              width: "100%",
              minWidth: "100%",
              maxWidth: "100%",
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {Array.from({ length: 6 }).map((_, index) => (
              <TemplateSkeleton key={`template-skeleton-${index}`} />
            ))}
          </Box>
        ) : (
          <Box
            sx={{
              flex: 1,
              overflow: "hidden",
              p: 0,
              width: "100%",
              minWidth: "100%",
              maxWidth: "100%",
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {filteredAndSortedTemplates.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "200px",
                  flexDirection: "column",
                  p: 1,
                  gap: 2,
                  width: "100%",
                  minWidth: "100%",
                }}
              >
                <Typography variant="h6" color="text.secondary">
                  📭 Nenhum template encontrado
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {templates.length === 0
                    ? "Não há templates disponíveis para esta empresa"
                    : searchTerm.trim() ||
                        statusFilter !== "all" ||
                        categoryFilter !== "all"
                      ? "Nenhum template encontrado com os filtros aplicados"
                      : "Nenhum template encontrado"}
                </Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  p: 1,
                  gap: 1,
                  overflowY: "auto",
                  overflowX: "hidden",
                  flex: 1,
                  "&::-webkit-scrollbar": {
                    width: "6px",
                  },
                  "&::-webkit-scrollbar-track": {
                    background: "#f1f1f1",
                    borderRadius: "3px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: "#c1c1c1",
                    borderRadius: "3px",
                    "&:hover": {
                      background: "#a8a8a8",
                    },
                  },
                }}
              >
                {filteredAndSortedTemplates.map((template) => {
                  const assignedCampaigns = getAssignedCampaigns(template.id);
                  return (
                    <Paper
                      key={template.id}
                      sx={{
                        p: 1.5,
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        border: "1px solid #e9edef",
                        borderRadius: "8px",
                        width: "100%",
                        maxWidth: "100%",
                        boxSizing: "border-box",
                        "&:hover": {
                          backgroundColor: "#f5f6f6",
                          borderColor: "#00a884",
                          boxShadow: "0 2px 8px rgba(0, 168, 132, 0.1)",
                        },
                      }}
                      onClick={() => handleShowDetails(template)}
                    >
                      {/* Header do card */}
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          mb: 1.5,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                          }}
                        >
                          <CategoryIcon category={template.category}>
                            {getCategoryIcon(template.category)}
                          </CategoryIcon>
                          <Box>
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: 600,
                                color: "#111b21",
                                fontSize: "13px",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                maxWidth: "200px",
                              }}
                            >
                              {template.name}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "#667781",
                                fontSize: "11px",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                maxWidth: "200px",
                              }}
                            >
                              {getCategoryName(template.category)} •{" "}
                              {getLanguageName(template.language)}
                            </Typography>
                          </Box>
                        </Box>
                        <Chip
                          label={
                            template.status === "APPROVED"
                              ? "Aprovado"
                              : template.status === "PENDING"
                                ? "Pendente"
                                : "Rejeitado"
                          }
                          color={
                            template.status === "APPROVED"
                              ? "success"
                              : template.status === "PENDING"
                                ? "warning"
                                : "error"
                          }
                          size="small"
                          sx={{
                            fontSize: "11px",
                            height: "24px",
                          }}
                        />
                      </Box>

                      {/* Informações principais */}
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mb: 1,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                          }}
                        >
                          {/* Campanhas atribuídas */}
                          {assignedCampaigns.length > 0 && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: "#00a884",
                                fontSize: "10px",
                                fontWeight: 500,
                                backgroundColor: "rgba(0, 168, 132, 0.1)",
                                padding: "2px 6px",
                                borderRadius: "4px",
                              }}
                            >
                              {assignedCampaigns.length} campanha
                              {assignedCampaigns.length > 1 ? "s" : ""}
                            </Typography>
                          )}
                        </Box>

                        {/* Botões de ação */}
                        <Box
                          sx={{
                            display: "flex",
                            gap: 0.5,
                          }}
                        >
                          {account?.role === "ADMINISTRATOR" &&
                            hasTemplateVariables(template) && (
                              <Tooltip title="Configurar parâmetros">
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleConfigureParameters(template);
                                  }}
                                  sx={{
                                    minWidth: "auto",
                                    width: 24,
                                    height: 24,
                                    p: 0,
                                    borderColor: "#00a884",
                                    color: "#00a884",
                                    "&:hover": {
                                      backgroundColor: "rgba(0, 168, 132, 0.1)",
                                      borderColor: "#008f6f",
                                    },
                                  }}
                                >
                                  <SettingsIcon
                                    sx={{
                                      fontSize: 14,
                                    }}
                                  />
                                </Button>
                              </Tooltip>
                            )}
                          {account?.role === "ADMINISTRATOR" && (
                            <Tooltip title="Atribuir à campanha">
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleAssignTemplate(template);
                                }}
                                disabled={assigning}
                                sx={{
                                  minWidth: "auto",
                                  width: 24,
                                  height: 24,
                                  p: 0,
                                  borderColor: "#00a884",
                                  color: "#00a884",
                                  "&:hover": {
                                    backgroundColor: "rgba(0, 168, 132, 0.1)",
                                    borderColor: "#008f6f",
                                  },
                                }}
                              >
                                <AddIcon
                                  sx={{
                                    fontSize: 14,
                                  }}
                                />
                              </Button>
                            </Tooltip>
                          )}
                        </Box>
                      </Box>

                      {/* Footer com informações adicionais - apenas quando há campanhas atribuídas */}
                      {assignedCampaigns.length > 0 && (
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            alignItems: "center",
                            pt: 1,
                            borderTop: "1px solid #f0f2f5",
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              color: "#667781",
                              fontSize: "10px",
                            }}
                          >
                            {assignedCampaigns
                              .map((c) => c.campaignName)
                              .join(", ")}
                          </Typography>
                        </Box>
                      )}
                    </Paper>
                  );
                })}
              </Box>
            )}
          </Box>
        )}

        {/* Paginação igual ao ChatSidebar */}
        {!loading && (
          <Box
            sx={{
              p: 2,
              textAlign: "center",
              borderTop: "1px solid #f0f0f0",
              backgroundColor: "#fff",
              position: "sticky",
              bottom: 0,
              zIndex: 11,
            }}
          >
            <Box
              sx={{
                display: "flex",
                gap: 1,
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <button
                onClick={goToPreviousPage}
                disabled={loading || currentPage <= 1}
                style={{
                  background:
                    currentPage <= 1 || loading ? "#f5f5f5" : "#00a884",
                  color: currentPage <= 1 || loading ? "#999" : "white",
                  border:
                    currentPage <= 1 || loading ? "1px solid #e0e0e0" : "none",
                  borderRadius: "6px",
                  padding: "8px 12px",
                  cursor:
                    currentPage <= 1 || loading ? "not-allowed" : "pointer",
                  fontSize: "12px",
                  fontWeight: "500",
                  transition: "all 0.2s ease",
                  opacity: currentPage <= 1 || loading ? 0.6 : 1,
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
                onMouseOver={(e) => {
                  if (currentPage > 1 && !loading) {
                    e.currentTarget.style.backgroundColor = "#008f6f";
                  }
                }}
                onFocus={(e) => {
                  if (currentPage > 1 && !loading) {
                    e.currentTarget.style.backgroundColor = "#008f6f";
                  }
                }}
                onMouseOut={(e) => {
                  if (currentPage > 1 && !loading) {
                    e.currentTarget.style.backgroundColor = "#00a884";
                  }
                }}
                onBlur={(e) => {
                  if (currentPage > 1 && !loading) {
                    e.currentTarget.style.backgroundColor = "#00a884";
                  }
                }}
              >
                {loading && currentPage > 1 ? (
                  <>
                    <CircularProgress size={12} sx={{ color: "white" }} />
                    Carregando...
                  </>
                ) : (
                  "← Anterior"
                )}
              </button>

              <span
                style={{
                  fontSize: "12px",
                  color: "#666",
                  padding: "0 8px",
                }}
              >
                {currentPage} de {totalPages}
              </span>

              <button
                onClick={goToNextPage}
                disabled={loading || currentPage >= totalPages}
                style={{
                  background:
                    currentPage >= totalPages || loading
                      ? "#f5f5f5"
                      : "#00a884",
                  color:
                    currentPage >= totalPages || loading ? "#999" : "white",
                  border:
                    currentPage >= totalPages || loading
                      ? "1px solid #e0e0e0"
                      : "none",
                  borderRadius: "6px",
                  padding: "8px 12px",
                  cursor:
                    currentPage >= totalPages || loading
                      ? "not-allowed"
                      : "pointer",
                  fontSize: "12px",
                  fontWeight: "500",
                  transition: "all 0.2s ease",
                  opacity: currentPage >= totalPages || loading ? 0.6 : 1,
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
                onMouseOver={(e) => {
                  if (currentPage < totalPages && !loading) {
                    e.currentTarget.style.backgroundColor = "#008f6f";
                  }
                }}
                onFocus={(e) => {
                  if (currentPage < totalPages && !loading) {
                    e.currentTarget.style.backgroundColor = "#008f6f";
                  }
                }}
                onMouseOut={(e) => {
                  if (currentPage < totalPages && !loading) {
                    e.currentTarget.style.backgroundColor = "#00a884";
                  }
                }}
                onBlur={(e) => {
                  if (currentPage < totalPages && !loading) {
                    e.currentTarget.style.backgroundColor = "#00a884";
                  }
                }}
              >
                {loading && currentPage < totalPages ? (
                  <>
                    <CircularProgress size={12} sx={{ color: "white" }} />
                    Carregando...
                  </>
                ) : (
                  "Próximo →"
                )}
              </button>
            </Box>
          </Box>
        )}
      </ContentContainer>

      {/* Dialog para atribuir template */}
      <Dialog
        open={showAssignmentDialog}
        onClose={() => !assigning && setShowAssignmentDialog(false)}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown={assigning}
        slotProps={{
          paper: {
            sx: {
              borderRadius: "12px",
              minWidth: "450px",
              maxWidth: "500px",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
              overflow: "hidden",
              animation: "modalSlideIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              "@keyframes modalSlideIn": {
                "0%": {
                  opacity: 0,
                  transform: "scale(0.95) translateY(-10px)",
                },
                "100%": {
                  opacity: 1,
                  transform: "scale(1) translateY(0)",
                },
              },
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            p: 2.5,
            pb: 2,
            borderBottom: "1px solid #e9edef",
            backgroundColor: "#f8f9fa",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            position: "relative",
          }}
        >
          {/* Elemento decorativo sutil */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              right: 0,
              width: 100,
              height: 100,
              background:
                "radial-gradient(circle, rgba(0, 168, 132, 0.06) 0%, transparent 70%)",
              borderRadius: "50%",
              transform: "translate(25px, -25px)",
            }}
          />

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              position: "relative",
              zIndex: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 40,
                height: 40,
                borderRadius: "10px",
                backgroundColor: "#e8f5e8",
                color: "#008069",
              }}
            >
              <CampaignIcon sx={{ fontSize: 20 }} />
            </Box>
            <Typography
              variant="h6"
              sx={{
                color: "#111b21",
                fontWeight: 600,
                fontSize: "17px",
              }}
            >
              Atribuir Template à Campanha
            </Typography>
          </Box>
          <IconButton
            onClick={() => !assigning && setShowAssignmentDialog(false)}
            disabled={assigning}
            sx={{
              position: "relative",
              zIndex: 1,
              color: "#667781",
              "&:hover": {
                backgroundColor: "#e9edef",
                color: "#111b21",
              },
              transition: "all 0.2s ease",
            }}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3, "&.MuiDialogContent-root": { pt: 3 } }}>
          {/* Template Preview Section */}
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="body2"
              sx={{
                mb: 1.5,
                color: "#667781",
                fontWeight: 600,
                fontSize: "12px",
                textTransform: "uppercase",
                letterSpacing: "0.8px",
                display: "flex",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              Template Selecionado
            </Typography>

            <Paper
              sx={{
                p: 1.5,
                border: "1px solid #e9edef",
                borderRadius: "8px",
                backgroundColor: "#ffffff",
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: "#f5f6f6",
                  borderColor: "#00a884",
                  boxShadow: "0 2px 8px rgba(0, 168, 132, 0.1)",
                },
              }}
            >
              {/* Header do card */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  mb: 1.5,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                  }}
                >
                  <CategoryIcon category={selectedTemplate?.category || ""}>
                    {getCategoryIcon(selectedTemplate?.category || "")}
                  </CategoryIcon>
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 600,
                        color: "#111b21",
                        fontSize: "13px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        maxWidth: "200px",
                      }}
                    >
                      {selectedTemplate?.name}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#667781",
                        fontSize: "11px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        maxWidth: "200px",
                      }}
                    >
                      {selectedTemplate
                        ? `${getCategoryName(selectedTemplate.category)} • ${getLanguageName(selectedTemplate.language)}`
                        : ""}
                    </Typography>
                  </Box>
                </Box>
                {selectedTemplate && (
                  <Chip
                    label={
                      selectedTemplate.status === "APPROVED"
                        ? "Aprovado"
                        : selectedTemplate.status === "PENDING"
                          ? "Pendente"
                          : "Rejeitado"
                    }
                    color={
                      selectedTemplate.status === "APPROVED"
                        ? "success"
                        : selectedTemplate.status === "PENDING"
                          ? "warning"
                          : "error"
                    }
                    size="small"
                    sx={{
                      fontSize: "11px",
                      height: "24px",
                    }}
                  />
                )}
              </Box>
            </Paper>
          </Box>

          {/* Campaign Selection Section */}
          <Box>
            <Typography
              sx={{
                color: "#111b21",
                mb: 1.5,
                fontWeight: 600,
                fontSize: "14px",
                display: "flex",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              <CampaignIcon sx={{ fontSize: 18, color: "#008069" }} />
              Selecione a Campanha
            </Typography>
            <FormControl fullWidth>
              <Select
                value={selectedCampaign}
                onChange={(e) => setSelectedCampaign(e.target.value)}
                disabled={assigning}
                IconComponent={KeyboardArrowDownIcon}
                sx={{
                  borderRadius: "10px",
                  backgroundColor: "#ffffff",
                  border: "1px solid #e9edef",
                  "& .MuiOutlinedInput-notchedOutline": {
                    border: "none",
                  },
                  "&:hover": {
                    borderColor: "#00a884",
                    backgroundColor: "#f8f9fa",
                  },
                  "&.Mui-focused": {
                    borderColor: "#00a884",
                    backgroundColor: "#ffffff",
                    boxShadow: "0 0 0 3px rgba(0, 168, 132, 0.1)",
                  },
                  "& .MuiSelect-select": {
                    padding: "12px 14px",
                    fontSize: "14px",
                    color: "#111b21",
                    fontWeight: 500,
                    display: "flex",
                    alignItems: "center",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: "#f8f9fa",
                    borderColor: "#e9edef",
                  },
                  transition: "all 0.2s ease",
                }}
                MenuProps={{
                  slotProps: {
                    paper: {
                      sx: {
                        borderRadius: "10px",
                        marginTop: "8px",
                        boxShadow: "0 4px 16px rgba(0, 0, 0, 0.1)",
                        border: "1px solid #e9edef",
                        maxHeight: "300px",
                      },
                    },
                  },
                  MenuListProps: {
                    sx: {
                      padding: "4px",
                    },
                  },
                }}
              >
                {campaigns && campaigns.length > 0 ? (
                  campaigns
                    .filter(
                      (campaign) => campaign && campaign.id && campaign.name,
                    )
                    .map((campaign) => (
                      <MenuItem
                        key={campaign.id}
                        value={campaign.id}
                        sx={{
                          borderRadius: "8px",
                          margin: "2px",
                          padding: "10px 14px",
                          fontSize: "14px",
                          "&:hover": {
                            backgroundColor: "#f0f2f5",
                          },
                          "&.Mui-selected": {
                            backgroundColor: "#e8f5e8",
                            color: "#008069",
                            fontWeight: 600,
                            "&:hover": {
                              backgroundColor: "#d1fae5",
                            },
                          },
                          transition: "all 0.15s ease",
                        }}
                      >
                        <CampaignIcon
                          sx={{
                            fontSize: 18,
                            mr: 1.5,
                            color: "inherit",
                          }}
                        />
                        {campaign.name}
                      </MenuItem>
                    ))
                ) : (
                  <MenuItem value="" disabled>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        py: 1,
                      }}
                    >
                      <CampaignIcon
                        sx={{
                          fontSize: 18,
                          color: "#9ca3af",
                        }}
                      />
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "14px",
                        }}
                      >
                        Nenhuma campanha disponível
                      </Typography>
                    </Box>
                  </MenuItem>
                )}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            p: 2.5,
            pt: 2,
            borderTop: "1px solid #e9edef",
            backgroundColor: "#f8f9fa",
            justifyContent: "flex-end",
            gap: 1.5,
          }}
        >
          <Button
            onClick={() => setShowAssignmentDialog(false)}
            disabled={assigning}
            sx={{
              color: "#667781",
              fontWeight: 500,
              fontSize: "14px",
              textTransform: "none",
              px: 2.5,
              py: 1,
              borderRadius: "8px",
              "&:hover": {
                backgroundColor: "#e9edef",
                color: "#111b21",
              },
              "&:disabled": {
                color: "#9ca3af",
              },
              transition: "all 0.2s ease",
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmAssignment}
            disabled={!selectedCampaign || assigning}
            variant="contained"
            startIcon={
              assigning ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <CheckCircleIcon />
              )
            }
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
              fontSize: "14px",
              px: 3,
              py: 1,
              backgroundColor: "#00a884",
              boxShadow: "0 2px 8px rgba(0, 168, 132, 0.25)",
              "&:hover": {
                backgroundColor: "#008f6f",
                boxShadow: "0 4px 12px rgba(0, 168, 132, 0.35)",
                transform: "translateY(-1px)",
              },
              "&:disabled": {
                backgroundColor: "#e9edef",
                color: "#9ca3af",
                boxShadow: "none",
                transform: "none",
              },
              "& .MuiButton-startIcon": {
                marginRight: assigning ? 1 : 0.5,
                marginLeft: 0,
              },
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          >
            {assigning ? "Atribuindo..." : "Atribuir"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de confirmação de remoção */}
      <Dialog
        open={showRemoveConfirm}
        onClose={() => !removingTemplate && setShowRemoveConfirm(false)}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown={!!removingTemplate}
        slotProps={{
          paper: {
            sx: {
              borderRadius: "12px",
              minWidth: "420px",
              maxWidth: "480px",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
              overflow: "hidden",
              animation: "modalSlideIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              "@keyframes modalSlideIn": {
                "0%": {
                  opacity: 0,
                  transform: "scale(0.95) translateY(-10px)",
                },
                "100%": {
                  opacity: 1,
                  transform: "scale(1) translateY(0)",
                },
              },
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            p: 2.5,
            pb: 2,
            borderBottom: "1px solid #e9edef",
            backgroundColor: "#fff8e1",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            position: "relative",
          }}
        >
          {/* Elemento decorativo sutil */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              right: 0,
              width: 100,
              height: 100,
              background:
                "radial-gradient(circle, rgba(255, 152, 0, 0.08) 0%, transparent 70%)",
              borderRadius: "50%",
              transform: "translate(25px, -25px)",
            }}
          />

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              position: "relative",
              zIndex: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 40,
                height: 40,
                borderRadius: "10px",
                backgroundColor: "#fff3e0",
                color: "#f57c00",
              }}
            >
              <WarningIcon sx={{ fontSize: 20 }} />
            </Box>
            <Typography
              variant="h6"
              sx={{
                color: "#111b21",
                fontWeight: 600,
                fontSize: "17px",
              }}
            >
              Confirmar Remoção
            </Typography>
          </Box>
          <IconButton
            onClick={() => !removingTemplate && setShowRemoveConfirm(false)}
            disabled={!!removingTemplate}
            sx={{
              position: "relative",
              zIndex: 1,
              color: "#667781",
              "&:hover": {
                backgroundColor: "#e9edef",
                color: "#111b21",
              },
              transition: "all 0.2s ease",
            }}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3, "&.MuiDialogContent-root": { pt: 3 } }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <Typography
              variant="body1"
              sx={{
                color: "#111b21",
                fontWeight: 500,
                fontSize: "15px",
                lineHeight: 1.5,
              }}
            >
              Tem certeza que deseja remover este template da campanha?
            </Typography>
            <Box
              sx={{
                p: 2,
                backgroundColor: "#fff8e1",
                borderRadius: "8px",
                border: "1px solid #ffe0b2",
                display: "flex",
                alignItems: "flex-start",
                gap: 1.5,
              }}
            >
              <WarningIcon
                sx={{
                  color: "#f57c00",
                  fontSize: 20,
                  mt: 0.25,
                  flexShrink: 0,
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  color: "#856404",
                  fontSize: "13px",
                  lineHeight: 1.5,
                }}
              >
                <strong>Esta ação não pode ser desfeita.</strong> O template
                será removido da campanha selecionada.
              </Typography>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            p: 2.5,
            pt: 2,
            borderTop: "1px solid #e9edef",
            backgroundColor: "#f8f9fa",
            justifyContent: "flex-end",
            gap: 1.5,
          }}
        >
          <Button
            onClick={() => setShowRemoveConfirm(false)}
            disabled={!!removingTemplate}
            sx={{
              color: "#667781",
              fontWeight: 500,
              fontSize: "14px",
              textTransform: "none",
              px: 2.5,
              py: 1,
              borderRadius: "8px",
              "&:hover": {
                backgroundColor: "#e9edef",
                color: "#111b21",
              },
              "&:disabled": {
                color: "#9ca3af",
              },
              transition: "all 0.2s ease",
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={confirmRemoveTemplate}
            disabled={!!removingTemplate}
            variant="contained"
            startIcon={
              removingTemplate ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <DeleteIcon />
              )
            }
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
              fontSize: "14px",
              px: 3,
              py: 1,
              backgroundColor: "#f44336",
              boxShadow: "0 2px 8px rgba(244, 67, 54, 0.25)",
              "&:hover": {
                backgroundColor: "#d32f2f",
                boxShadow: "0 4px 12px rgba(244, 67, 54, 0.35)",
                transform: "translateY(-1px)",
              },
              "&:disabled": {
                backgroundColor: "#e9edef",
                color: "#9ca3af",
                boxShadow: "none",
                transform: "none",
              },
              "& .MuiButton-startIcon": {
                marginRight: removingTemplate ? 1 : 0.5,
                marginLeft: 0,
              },
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          >
            {removingTemplate ? "Removendo..." : "Remover"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de detalhes do template - melhorado */}
      <Dialog
        open={showDetails}
        onClose={handleCloseDetails}
        maxWidth="md"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: "12px",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
              overflow: "hidden",
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            p: 3,
            borderBottom: "1px solid #e9edef",
            background: "linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)",
            position: "relative",
          }}
        >
          {/* Elemento decorativo sutil */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              right: 0,
              width: 120,
              height: 120,
              background:
                "radial-gradient(circle, rgba(0, 168, 132, 0.05) 0%, transparent 70%)",
              borderRadius: "50%",
              transform: "translate(30px, -30px)",
            }}
          />

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              position: "relative",
              zIndex: 1,
            }}
          >
            {selectedTemplate && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 48,
                  height: 48,
                  borderRadius: "12px",
                  backgroundColor:
                    selectedTemplate.status === "APPROVED"
                      ? "#e8f5e8"
                      : selectedTemplate.status === "PENDING"
                        ? "#fff3e0"
                        : "#ffebee",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                  border: "1px solid rgba(255, 255, 255, 0.8)",
                }}
              >
                <CategoryIcon category={selectedTemplate.category}>
                  {getCategoryIcon(selectedTemplate.category)}
                </CategoryIcon>
              </Box>
            )}
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: "#111b21",
                  mb: 0.5,
                  fontSize: "18px",
                }}
              >
                {selectedTemplate?.name}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "#667781",
                  fontSize: "14px",
                  fontWeight: 500,
                }}
              >
                {selectedTemplate &&
                  `${getCategoryName(selectedTemplate.category)} • ${getLanguageName(selectedTemplate.language)}`}
              </Typography>
            </Box>
            {selectedTemplate && (
              <Chip
                label={
                  selectedTemplate.status === "APPROVED"
                    ? "Aprovado"
                    : selectedTemplate.status === "PENDING"
                      ? "Pendente"
                      : "Rejeitado"
                }
                color={
                  selectedTemplate.status === "APPROVED"
                    ? "success"
                    : selectedTemplate.status === "PENDING"
                      ? "warning"
                      : "error"
                }
                size="small"
                sx={{
                  fontSize: "12px",
                  fontWeight: 600,
                  height: "28px",
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                }}
              />
            )}
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {selectedTemplate && (
            <Box>
              {/* Seção de Informações */}
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    color: "#111b21",
                    mb: 1.5,
                    mt: 1.5,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    fontSize: "15px",
                  }}
                >
                  <InfoIcon sx={{ fontSize: 18, color: "#00a884" }} />
                  Informações do Template
                </Typography>

                {/* Layout em duas colunas com badges visuais */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "1fr 1fr",
                    },
                    gap: 1.5,
                  }}
                >
                  {/* Badge Nome */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.25,
                      p: 1.5,
                      backgroundColor: "#ffffff",
                      borderRadius: "10px",
                      border: "1px solid #e9edef",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        borderColor: "#00a884",
                        boxShadow: "0 2px 8px rgba(0, 168, 132, 0.08)",
                        transform: "translateY(-1px)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 40,
                        height: 40,
                        borderRadius: "10px",
                        backgroundColor: "#e8f5e8",
                        color: "#00a884",
                        flexShrink: 0,
                      }}
                    >
                      <TitleIcon sx={{ fontSize: 20 }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#667781",
                          fontSize: "10px",
                          textTransform: "uppercase",
                          fontWeight: 600,
                          letterSpacing: "0.5px",
                          display: "block",
                          mb: 0.5,
                        }}
                      >
                        Nome
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          color: "#111b21",
                          fontSize: "13px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {selectedTemplate.name}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Badge Status */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.25,
                      p: 1.5,
                      backgroundColor: "#ffffff",
                      borderRadius: "10px",
                      border: "1px solid #e9edef",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        borderColor:
                          selectedTemplate.status === "APPROVED"
                            ? "#4caf50"
                            : selectedTemplate.status === "PENDING"
                              ? "#ff9800"
                              : "#f44336",
                        boxShadow:
                          selectedTemplate.status === "APPROVED"
                            ? "0 2px 8px rgba(76, 175, 80, 0.08)"
                            : selectedTemplate.status === "PENDING"
                              ? "0 2px 8px rgba(255, 152, 0, 0.08)"
                              : "0 2px 8px rgba(244, 67, 54, 0.08)",
                        transform: "translateY(-1px)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 40,
                        height: 40,
                        borderRadius: "10px",
                        backgroundColor:
                          selectedTemplate.status === "APPROVED"
                            ? "#e8f5e8"
                            : selectedTemplate.status === "PENDING"
                              ? "#fff3e0"
                              : "#ffebee",
                        color:
                          selectedTemplate.status === "APPROVED"
                            ? "#4caf50"
                            : selectedTemplate.status === "PENDING"
                              ? "#ff9800"
                              : "#f44336",
                        flexShrink: 0,
                      }}
                    >
                      <CheckCircleIcon sx={{ fontSize: 20 }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#667781",
                          fontSize: "10px",
                          textTransform: "uppercase",
                          fontWeight: 600,
                          letterSpacing: "0.5px",
                          display: "block",
                          mb: 0.5,
                        }}
                      >
                        Status
                      </Typography>
                      <Chip
                        label={
                          selectedTemplate.status === "APPROVED"
                            ? "Aprovado"
                            : selectedTemplate.status === "PENDING"
                              ? "Pendente"
                              : "Rejeitado"
                        }
                        color={
                          selectedTemplate.status === "APPROVED"
                            ? "success"
                            : selectedTemplate.status === "PENDING"
                              ? "warning"
                              : "error"
                        }
                        size="small"
                        sx={{
                          fontSize: "11px",
                          fontWeight: 600,
                          height: "22px",
                        }}
                      />
                    </Box>
                  </Box>

                  {/* Badge Categoria */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.25,
                      p: 1.5,
                      backgroundColor: "#ffffff",
                      borderRadius: "10px",
                      border: "1px solid #e9edef",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        borderColor: "#00a884",
                        boxShadow: "0 2px 8px rgba(0, 168, 132, 0.08)",
                        transform: "translateY(-1px)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 40,
                        height: 40,
                        flexShrink: 0,
                      }}
                    >
                      <CategoryIcon
                        category={selectedTemplate.category}
                        sx={{
                          width: "32px",
                          height: "32px",
                          fontSize: "16px",
                        }}
                      >
                        {getCategoryIcon(selectedTemplate.category)}
                      </CategoryIcon>
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#667781",
                          fontSize: "10px",
                          textTransform: "uppercase",
                          fontWeight: 600,
                          letterSpacing: "0.5px",
                          display: "block",
                          mb: 0.5,
                        }}
                      >
                        Categoria
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          color: "#111b21",
                          fontSize: "13px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {getCategoryName(selectedTemplate.category)}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Badge Idioma */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.25,
                      p: 1.5,
                      backgroundColor: "#ffffff",
                      borderRadius: "10px",
                      border: "1px solid #e9edef",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        borderColor: "#00a884",
                        boxShadow: "0 2px 8px rgba(0, 168, 132, 0.08)",
                        transform: "translateY(-1px)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 40,
                        height: 40,
                        borderRadius: "10px",
                        backgroundColor: "#f3e5f5",
                        color: "#9c27b0",
                        flexShrink: 0,
                      }}
                    >
                      <LanguageIcon sx={{ fontSize: 20 }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#667781",
                          fontSize: "10px",
                          textTransform: "uppercase",
                          fontWeight: 600,
                          letterSpacing: "0.5px",
                          display: "block",
                          mb: 0.5,
                        }}
                      >
                        Idioma
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          color: "#111b21",
                          fontSize: "13px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {getLanguageName(selectedTemplate.language)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>

              {/* Seção de Campanhas Atribuídas */}
              {getAssignedCampaigns(selectedTemplate.id).length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      color: "#111b21",
                      mb: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    📋 Campanhas Atribuídas
                  </Typography>

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                    }}
                  >
                    {getAssignedCampaigns(selectedTemplate.id).map(
                      (campaign) => (
                        <Box
                          key={campaign.campaignId}
                          sx={{
                            p: 2,
                            backgroundColor: "#e8f5e8",
                            borderRadius: "8px",
                            border: "1px solid #c8e6c9",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 500,
                              color: "#2e7d32",
                            }}
                          >
                            {campaign.campaignName}
                          </Typography>
                          {account?.role?.toUpperCase() === "ADMINISTRATOR" && (
                            <Button
                              size="small"
                              variant="text"
                              color="error"
                              onClick={() => {
                                handleRemoveTemplate(
                                  selectedTemplate.id,
                                  campaign.campaignId,
                                );
                                handleCloseDetails();
                              }}
                              disabled={
                                removingTemplate === selectedTemplate.id
                              }
                              sx={{
                                minWidth: "auto",
                                p: 0.5,
                              }}
                            >
                              {removingTemplate === selectedTemplate.id ? (
                                <CircularProgress size={16} />
                              ) : (
                                <DeleteIcon fontSize="small" />
                              )}
                            </Button>
                          )}
                        </Box>
                      ),
                    )}
                  </Box>
                </Box>
              )}

              {/* Seção de Preview do Template */}
              {selectedTemplate && (
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      color: "#111b21",
                      mb: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    📱 Preview do Template
                  </Typography>

                  {loadingParameters ? (
                    <Box
                      sx={{
                        p: 3,
                        backgroundColor: "#f8f9fa",
                        borderRadius: "8px",
                        border: "1px solid #e9edef",
                        textAlign: "center",
                      }}
                    >
                      <CircularProgress
                        size={24}
                        sx={{ color: "#00a884", mb: 2 }}
                      />
                      <Typography variant="body2" sx={{ color: "#667781" }}>
                        Carregando preview do template...
                      </Typography>
                    </Box>
                  ) : selectedTemplate && templateParameters ? (
                    <Box
                      sx={{
                        p: 2,
                        backgroundColor: "#f8f9fa",
                        borderRadius: "8px",
                        border: "1px solid #e9edef",
                      }}
                    >
                      <TemplatePreview
                        selectedTemplate={selectedTemplate}
                        templateParameters={templateParameters}
                        parameterConfigs={parameterConfigs}
                        attendantName={account?.username || "Admin"}
                      />
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        p: 3,
                        backgroundColor: "#f8f9fa",
                        borderRadius: "8px",
                        border: "1px solid #e9edef",
                        textAlign: "center",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#667781",
                          fontStyle: "italic",
                        }}
                      >
                        Preview não disponível para este template
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            p: 2,
            borderTop: "1px solid #e9edef",
            background: "linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)",
            justifyContent: "center",
          }}
        >
          <Button
            onClick={handleCloseDetails}
            variant="contained"
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
              px: 4,
              py: 1.2,
              fontSize: "13px",
              backgroundColor: "#00a884",
              boxShadow: "0 2px 8px rgba(0, 168, 132, 0.3)",
              "&:hover": {
                backgroundColor: "#008f6f",
                boxShadow: "0 4px 12px rgba(0, 168, 132, 0.4)",
                transform: "translateY(-1px)",
              },
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Configurador de Parâmetros de Template */}
      <TemplateParameterConfigurator
        template={templateForConfig}
        isOpen={showParameterConfig}
        onClose={handleCloseParameterConfig}
        onSave={handleSaveParameterConfig}
      />
    </Container>
  );
};

export default TemplatesSidebar;
